/**
 * Hooks de Perfiles de Configuracion (ADR_012).
 *
 * Patron react-query igual que useConfig.ts y useTelegram.ts.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  ConfigDeviceFilters,
  ConfigDeviceList,
  ConfigurationProfile,
  ConfigurationProfileList,
  CreateConfigurationProfileRequest,
  DeviceTypeCatalogResponse,
  FieldCatalogResponse,
  ZabbixHostGroupList,
  ZabbixHostList,
} from "@/api/types";

// ── Catalogo de device_types con filtros cascada (PR-CMgmt-H) ───────────

export function useDeviceTypeCatalog() {
  return useQuery({
    queryKey: ["config", "device-types"],
    queryFn: ({ signal }) =>
      api<DeviceTypeCatalogResponse>("/api/v1/config/device-types", {
        signal,
      }),
    staleTime: Infinity, // catalogo no cambia
  });
}

// ── Catalogo de campos por device_type ──────────────────────────────────

export function useFieldCatalog(deviceType: string | null) {
  return useQuery({
    queryKey: ["config", "field-catalog", deviceType],
    queryFn: ({ signal }) =>
      api<FieldCatalogResponse>(
        `/api/v1/config/field-catalog/${deviceType}`,
        { signal },
      ),
    enabled: !!deviceType,
    staleTime: Infinity, // catalogo no cambia
  });
}

// ── Zabbix search ───────────────────────────────────────────────────────

export function useZabbixHostGroups(nameFilter?: string) {
  return useQuery({
    queryKey: ["config", "zabbix", "host-groups", nameFilter ?? null],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (nameFilter) params.set("name", nameFilter);
      const qs = params.toString();
      return api<ZabbixHostGroupList>(
        `/api/v1/config/zabbix/host-groups${qs ? `?${qs}` : ""}`,
        { signal },
      );
    },
    staleTime: 5 * 60_000, // 5 min, mismo TTL que el cache backend
  });
}

export function useZabbixHostsInGroup(
  hostGroupId: string | null,
  nameFilter?: string,
) {
  return useQuery({
    queryKey: [
      "config",
      "zabbix",
      "hosts",
      hostGroupId,
      nameFilter ?? null,
    ],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      params.set("host_group_id", hostGroupId!);
      if (nameFilter) params.set("name", nameFilter);
      return api<ZabbixHostList>(
        `/api/v1/config/zabbix/hosts?${params.toString()}`,
        { signal },
      );
    },
    enabled: !!hostGroupId,
    staleTime: 60_000,
  });
}

// ── Devices del inventario (no-Zabbix) ─────────────────────────────────

export function useConfigDevices(filters: ConfigDeviceFilters = {}) {
  return useQuery({
    queryKey: ["config", "devices", filters],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.device_type) params.set("device_type", filters.device_type);
      if (filters.site_id) params.set("site_id", filters.site_id);
      if (filters.status) params.set("status", filters.status);
      if (filters.page) params.set("page", String(filters.page));
      if (filters.per_page) params.set("per_page", String(filters.per_page));
      const qs = params.toString();
      return api<ConfigDeviceList>(
        `/api/v1/config/devices${qs ? `?${qs}` : ""}`,
        { signal },
      );
    },
    enabled: !!filters.device_type,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// ── Perfiles CRUD ───────────────────────────────────────────────────────

export function useConfigurationProfiles(filters: {
  status?: string;
  device_type?: string;
  include_deleted?: boolean;
  page?: number;
  per_page?: number;
} = {}) {
  return useQuery({
    queryKey: ["config", "profiles", filters],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.device_type)
        params.set("device_type", filters.device_type);
      if (filters.include_deleted)
        params.set("include_deleted", "true");
      if (filters.page) params.set("page", String(filters.page));
      if (filters.per_page) params.set("per_page", String(filters.per_page));
      const qs = params.toString();
      return api<ConfigurationProfileList>(
        `/api/v1/config/profiles${qs ? `?${qs}` : ""}`,
        { signal },
      );
    },
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useConfigurationProfile(profileId: string | null) {
  return useQuery({
    queryKey: ["config", "profile", profileId],
    queryFn: ({ signal }) =>
      api<ConfigurationProfile>(
        `/api/v1/config/profiles/${profileId}`,
        { signal },
      ),
    enabled: !!profileId,
  });
}

export function useCreateConfigurationProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateConfigurationProfileRequest) =>
      api<ConfigurationProfile>("/api/v1/config/profiles", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["config", "profiles"],
      });
    },
  });
}

/**
 * Pausar o reanudar un perfil exige el segundo factor desde el 1-sep-2026.
 *
 * Pausar apaga la cosecha de TODOS los equipos del perfil, y antes se hacia sin
 * dejar rastro. Ese dia el perfil de HikCentral aparecio en pausa y no hubo
 * forma de saber quien lo hizo. Ahora el codigo identifica al autor y el backend
 * escribe una fila en la bitacora de administracion.
 *
 * `totp_code` viaja en snake_case porque es el nombre del campo en el body; el
 * dialogo emite `totpCode` y la pagina hace la traduccion, igual que en rollback.
 */
export interface ProfilePauseArgs {
  profileId: string;
  totp_code: string;
  notes: string | null;
}

function useCambioDeEstado(accion: "pause" | "resume") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, totp_code, notes }: ProfilePauseArgs) =>
      api<ConfigurationProfile>(
        `/api/v1/config/profiles/${profileId}/${accion}`,
        { method: "POST", body: { totp_code, notes } },
      ),
    onSuccess: (_d, { profileId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["config", "profile", profileId],
      });
      void queryClient.invalidateQueries({ queryKey: ["config", "profiles"] });
    },
  });
}

export function usePauseProfile() {
  return useCambioDeEstado("pause");
}

export function useResumeProfile() {
  return useCambioDeEstado("resume");
}

export function useDeleteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) =>
      api<void>(`/api/v1/config/profiles/${profileId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["config", "profiles"] });
    },
  });
}

export function useSnapshotNowProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) =>
      api<{
        started: number;
        results: { ok: number; offline: number; error: number; dedupe: number };
        errors: { device_id: string; error: string }[];
      }>(`/api/v1/config/profiles/${profileId}/snapshot-now`, {
        method: "POST",
      }),
    onSuccess: (_d, profileId) => {
      void queryClient.invalidateQueries({
        queryKey: ["config", "profile", profileId],
      });
      void queryClient.invalidateQueries({ queryKey: ["config", "profiles"] });
    },
  });
}

export function useAddDevicesToProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      profileId,
      deviceIds,
    }: {
      profileId: string;
      deviceIds: string[];
    }) =>
      api<{
        added: number;
        skipped: number;
        missing_device_ids: string[];
      }>(`/api/v1/config/profiles/${profileId}/devices`, {
        method: "POST",
        body: { device_ids: deviceIds },
      }),
    onSuccess: (_data, { profileId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["config", "profile", profileId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["config", "profiles"],
      });
    },
  });
}
